import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { WriterToStudioUpgradeModal } from "components/common/modals/WriterToStudioUpgradeModal";
import { PATH_WORKSPACE } from "config";
import { resolveWorkspacePath } from "utils/routing/workspaceRouteUtils";
import {
  PROJECT_STEPS,
  SIDEBAR_PROJECT_LINKS,
  buildProjectHref,
  getProjectStepItemSegment,
  resolveProjectNavLocation,
  resolveProjectStepStatus,
  type ProjectStepId,
} from "config/navigation";
import { useWriterStudioUpgrade } from "hooks/project";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { useTrackNavClick } from "../useTrackNavClick";
import { ProjectNav, type ProjectNavLinkModel, type ProjectNavStepModel } from "./ProjectNav";
import { PROJECT_NAV_ITEM_PREFETCH } from "./projectNavPrefetch";
import { useSingleOpenStep } from "./useSingleOpenStep";

interface ProjectSidebarNavProps {
  workspaceId: string;
  project: ProjectState;
}

/**
 * Wires the project navigation to routes, statuses and the Studio lock.
 * It never starts or offers an AI run: each step's empty page does that.
 */
export const ProjectSidebarNav = ({ workspaceId, project }: ProjectSidebarNavProps) => {
  const { pathname } = useLocation();
  const trackNavClick = useTrackNavClick();
  const projectId = String(project.projectId);
  const studioUpgrade = useWriterStudioUpgrade(projectId, project);
  const studioLocked = studioUpgrade.needsStudioUpgrade;

  const location = resolveProjectNavLocation(pathname);
  const activeStepId = location.kind === "step" ? location.stepId : null;
  const activeItemId = location.kind === "step" ? location.itemId : null;
  const activeLinkId = location.kind === "link" ? location.linkId : null;

  const isLocked = useCallback(
    (stepId: ProjectStepId) =>
      studioLocked && PROJECT_STEPS.some((step) => step.id === stepId && step.studioOnly),
    [studioLocked]
  );
  const { openStepId, upsellStepId, toggleStep } = useSingleOpenStep(activeStepId, isLocked);

  const steps = useMemo<ProjectNavStepModel[]>(
    () =>
      PROJECT_STEPS.map((step) => ({
        id: step.id,
        label: step.label,
        icon: step.icon,
        status: resolveProjectStepStatus(step, project.featureStatuses, project.activeDocumentId),
        locked: isLocked(step.id),
        isCurrent: step.id === activeStepId,
        lockDescription: step.studioLockDescription ?? "",
        items: step.items.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          href: buildProjectHref(workspaceId, projectId, getProjectStepItemSegment(step, item)),
          active: step.id === activeStepId && item.id === activeItemId,
          prefetch: PROJECT_NAV_ITEM_PREFETCH[item.id],
        })),
      })),
    [
      activeItemId,
      activeStepId,
      isLocked,
      project.activeDocumentId,
      project.featureStatuses,
      projectId,
      workspaceId,
    ]
  );

  const links = useMemo<ProjectNavLinkModel[]>(
    () =>
      SIDEBAR_PROJECT_LINKS.map((link) => ({
        id: link.id,
        label: link.sidebarLabel,
        icon: link.icon,
        href: buildProjectHref(workspaceId, projectId, link.segment),
        active: link.id === activeLinkId,
      })),
    [activeLinkId, projectId, workspaceId]
  );

  return (
    <>
      <ProjectNav
        steps={steps}
        links={links}
        openStepId={openStepId}
        upsellStepId={upsellStepId}
        onToggleStep={toggleStep}
        onUpgrade={() => studioUpgrade.openWriterStudioUpgradeModal()}
        onItemClick={(stepId, itemId) =>
          trackNavClick({ area: "project", step: stepId, item: itemId })
        }
        onLinkClick={(linkId) => trackNavClick({ area: "project", item: linkId })}
        allProjectsHref={resolveWorkspacePath(PATH_WORKSPACE.workspaceProjects, workspaceId)}
        onAllProjectsClick={() => trackNavClick({ area: "project", item: "all-projects" })}
      />
      <WriterToStudioUpgradeModal
        open={studioUpgrade.writerStudioUpgradeModalOpen}
        onClose={studioUpgrade.closeWriterStudioUpgradeModal}
        projectId={studioUpgrade.projectId}
        projectName={studioUpgrade.projectName}
      />
    </>
  );
};
